import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { UserManagementFormContext } from '../../../../contexts/UserManagementFormContext'
import { AddSchema } from '../../schemas/add'
import { CREATE_EMPLOYEE, Data } from './api'
import { mapper } from './mapper'
import { UseCreateEmployee } from './types'

export const useCreateEmployee = (): UseCreateEmployee => {
  const navigate = useNavigate()
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(UserManagementFormContext)
  const [errors, setErrors] = useState<FieldErrors<AddSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: AddSchema }, Data>({
    mutation: CREATE_EMPLOYEE,
    onCompleted: async (data: Data): Promise<void> => {
      const {
        createEmployee: { errors, user },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.employee.add.error'), { variant: 'error' })
      } else if (user) {
        setErrors({})
        navigate(generatePath(ROUTES.USER.LIST, { tab: 'employees' }))
        enqueueSnackbar(t('form.employee.add.success'), { variant: 'success' })
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const createEmployee = (data: AddSchema): void => {
    void mutateAsync({
      input: mapper(data),
    })
  }

  return { createEmployee, errors }
}
